// Patient engagement — messages with the care team, the daily mood check-in, surveys and
// goals. Epic 12 / ACDPA-222, patient side (E12c, URS_12.6 un-deferred 2026-09-18).
//
// ⛔ EVERY CALL HERE IS TO THE **KERNEL**, not to blaze. That is the point of the slice, not an
// implementation detail: the portal's inbox, the care team's reply and the patient's goals all
// live in the kernel's tables, so a message this app sends through blaze would be a message no
// clinician ever sees. blaze has no thread, conversation, message or survey model at all — its
// only patient↔care-team surfaces are follower requests, journal posts and push notifications.
// See `.arc-board/PATIENT-TOUCHPOINTS.md` for the full split.
//
// ⛔ NOTHING HERE SENDS A `profile_id` OR AN `org_id`. Every route is `/me/engagement/…`: the
// kernel resolves the subject from the session and the organization from the patient's own live
// association. If you find yourself wanting to pass an id, the route you want does not exist.
//
// ⚠️ THE FEATURE FLAGS ARE NOT ADVISORY. `getEngagementFeatures()` tells the UI what to show,
// but the kernel enforces the same toggles on every write and answers 422 with a code
// (`MESSAGING_DISABLED`, `MOOD_RESPONSE_DISABLED`, `SURVEYS_DISABLED`) when a stale client tries
// anyway. Screens surface that message rather than swallowing it — a patient's words must never
// disappear silently.

import { kernelClient } from "./api";
import { ENGAGEMENT_ENDPOINTS } from "./endpoints";

// ── vocabulary ────────────────────────────────────────────────────────────────────────────

/**
 * One lookup row. ⭐ The kernel owns the wording (TD-82): render `label`, send `code` back.
 * Never hardcode a label in this app — a re-wording is a kernel migration, not a release here.
 */
export interface EngagementRef {
  code: string;
  label: string;
}

export interface MoodRef extends EngagementRef {
  /** 1 (very low) … 5 (very good). The order the picker renders in. */
  score: number;
  emoji: string | null;
}

export interface CadenceRef extends EngagementRef {
  /** Days after device assignment this survey is due — 30, 60, 90. */
  offset_days: number;
}

// ── ACDPA-228 / URS_12.6 — feature gating ─────────────────────────────────────────────────

/**
 * The engagement features in force for THIS patient, resolved from their organization's
 * toggles.
 *
 * ⚠️ `org_id === null` is a normal answer, not an error: the patient has no live association,
 * so there is nobody to message and every flag is false. Screens render an explanatory empty
 * state rather than a failure.
 */
export interface EngagementFeatures {
  org_id: number | null;
  org_name: string | null;
  location_name: string | null;
  messaging_enabled: boolean;
  mood_response_enabled: boolean;
  surveys_enabled: boolean;
}

/** Everything off — what the UI shows before the first response and when the call fails. */
export const NO_ENGAGEMENT_FEATURES: EngagementFeatures = {
  org_id: null,
  org_name: null,
  location_name: null,
  messaging_enabled: false,
  mood_response_enabled: false,
  surveys_enabled: false,
};

/**
 * ⭐ NOT CACHED, DELIBERATELY. URS_12.6 AC 2 is *"the mobile app receives flag changes
 * promptly"*; a client-side cache is precisely what makes an admin's toggle arrive late. The
 * response is six small fields, so re-reading it when a screen opens costs nothing worth saving.
 */
export const getEngagementFeatures = async (): Promise<EngagementFeatures> => {
  const { data } = await kernelClient.get(ENGAGEMENT_ENDPOINTS.FEATURES);
  return data as EngagementFeatures;
};

// ── URS_12.1 — messaging ──────────────────────────────────────────────────────────────────

export interface EngagementMessage {
  id: number;
  /** `patient` or `staff` — with its label, so the bubble can be attributed without a mapping. */
  sender: EngagementRef;
  sender_profile_id: number | null;
  body: string;
  sent_at: string;
  /** When the OTHER side read this turn. `null` on a staff reply the patient has not opened. */
  read_at: string | null;
}

export interface MessageThread {
  id: number;
  org_id: number;
  profile_id: number;
  subject: string | null;
  last_activity_at: string;
  messages: EngagementMessage[];
}

/**
 * A row on the conversation list, from the PATIENT's side.
 *
 * ⭐ `unread_from_care_team` IS THE BADGE ON THE THREAD — staff turns this patient has not
 * opened — and the server is the only thing that computes it. ⛔ Do not count bubbles in the
 * app: this route carries no message bodies (deliberately), and a client that kept its own
 * "seen" flag would disagree with the care team's read receipt the moment a reply landed
 * between two polls. It falls to 0 when `markThreadRead` lands, because the receipt and the
 * count are the same column.
 *
 * ⚠️ THE KERNEL'S **STAFF** THREAD MODEL HAS AN `unread_count` THAT MEANS THE OPPOSITE — the
 * PATIENT turns the care team has not read. It is not on this route, and this app must never
 * reach for a field by that name.
 */
export interface MyThreadSummary {
  id: number;
  org_id: number;
  subject: string | null;
  last_activity_at: string;
  message_count: number;
  unread_from_care_team: number;
}

export interface MyThreads {
  items: MyThreadSummary[];
  total: number;
  /** The badge on the Messages ENTRY — the sum of the items', computed server-side. */
  unread_total: number;
}

export const listMyThreads = async (): Promise<MyThreads> => {
  const { data } = await kernelClient.get(ENGAGEMENT_ENDPOINTS.THREADS);
  return data as MyThreads;
};

export const readMyThread = async (threadId: number): Promise<MessageThread> => {
  const { data } = await kernelClient.get(ENGAGEMENT_ENDPOINTS.THREAD(threadId));
  return data as MessageThread;
};

/** Start a new conversation. The inbox row the care team triages is created with it. */
export const startThread = async (
  body: string,
  subject?: string | null,
): Promise<MessageThread> => {
  const { data } = await kernelClient.post(ENGAGEMENT_ENDPOINTS.THREADS, {
    body,
    subject: subject ?? null,
  });
  return data as MessageThread;
};

/** Add a turn to an existing conversation. Reopens the care team's inbox item. */
export const replyInThread = async (
  threadId: number,
  body: string,
): Promise<EngagementMessage> => {
  const { data } = await kernelClient.post(
    ENGAGEMENT_ENDPOINTS.THREAD_MESSAGES(threadId),
    { body },
  );
  return data as EngagementMessage;
};

/**
 * Tell the care team their replies have been seen.
 *
 * ⛔ CALL THIS ONLY WHEN THE THREAD IS ACTUALLY ON SCREEN. It is a separate call from reading
 * the thread precisely so a prefetch cannot claim the patient read something. Idempotent.
 */
export const markThreadRead = async (
  threadId: number,
): Promise<{ thread_id: number; messages_marked_read: number }> => {
  const { data } = await kernelClient.post(ENGAGEMENT_ENDPOINTS.THREAD_READ(threadId));
  return data;
};

// ── URS_12.2 — the daily mood check-in ────────────────────────────────────────────────────

export interface MoodCheckin {
  id: number;
  profile_id: number;
  org_id: number;
  checkin_date: string;
  mood: MoodRef;
  note: string | null;
  source: EngagementRef;
  recorded_by_profile_id: number | null;
}

/**
 * My check-in history AND the day the server says it is for me.
 *
 * ⛔ `today` IS WHY THIS RETURNS THE ENVELOPE AND NOT JUST THE ROWS (E12d/R3). The app used to
 * derive "today" from the device clock; the kernel now resolves the patient's own local day
 * from `profile.time_zone` and says so here, and that is the date
 * `MOOD_CHECKIN_ALREADY_RECORDED` keys on. Comparing `checkin_date` against anything else is
 * how the app ends up offering a check-in the server will refuse.
 */
export interface MyMoodCheckins {
  items: MoodCheckin[];
  total: number;
  /** `YYYY-MM-DD` — the PATIENT's local day, decided server-side. */
  today: string;
}

export const listMyMoodCheckins = async (since?: string): Promise<MyMoodCheckins> => {
  const { data } = await kernelClient.get(ENGAGEMENT_ENDPOINTS.MOOD_CHECKINS, {
    params: since ? { since } : undefined,
  });
  return {
    items: (data?.items ?? []) as MoodCheckin[],
    total: (data?.total ?? 0) as number,
    today: data?.today as string,
  };
};

/**
 * Log today's mood.
 *
 * ⛔ **NO DATE IS SENT, AND REMOVING IT WAS THE POINT OF E12d/R3.** The app used to send the
 * device's local date because the kernel defaulted to today in **UTC** — two clocks deciding
 * one clinical fact, so a phone set wrong, or a patient who travelled, filed a check-in against
 * a day the server disagreed with. The kernel now resolves the patient's own local day from
 * `profile.time_zone` (the same rule the therapy projection uses). ⚠️ Do not reintroduce a
 * `checkin_date` argument here: the field still exists on the wire, but it is for an OFFLINE
 * queue naming the day it was composed on, which this app does not have.
 *
 * ⛔ THROWS on 409 `MOOD_CHECKIN_ALREADY_RECORDED` (one per day) and on 422
 * `MOOD_RESPONSE_DISABLED` (a note while the org has written responses off). Both are answers
 * the screen must show, not conditions to swallow.
 */
export const recordMoodCheckin = async (
  mood: string,
  note: string | null,
): Promise<MoodCheckin> => {
  const { data } = await kernelClient.post(ENGAGEMENT_ENDPOINTS.MOOD_CHECKINS, {
    mood,
    note: note && note.trim() ? note.trim() : null,
  });
  return data as MoodCheckin;
};

/**
 * Change TODAY's own check-in — mood and note — in place (F-483, UAT3).
 *
 * ⛔ THE SERVER DECIDES WHETHER IT IS STILL "TODAY". The kernel refuses anything but the
 * patient's own local day with 409 `MOOD_CHECKIN_EDIT_WINDOW_CLOSED`, and a row that is not the
 * patient's own app check-in with 404 `MOOD_CHECKIN_NOT_FOUND`; the screen offers Edit only when
 * `checkin_date === today` from `listMyMoodCheckins`, so both are backstops, not the gate.
 *
 * ⚠️ `note: undefined` LEAVES THE NOTE ALONE (the field is not sent) — what the screen passes
 * when the org has written responses off and no box is shown. A string that is blank CLEARS it
 * (the patient deleted their words). `MOOD_RESPONSE_DISABLED` applies exactly as on create.
 */
export const updateMoodCheckin = async (
  checkinId: number,
  mood: string,
  note: string | null | undefined,
): Promise<MoodCheckin> => {
  const body: { mood: string; note?: string | null } = { mood };
  if (note !== undefined) body.note = note && note.trim() ? note.trim() : null;
  const { data } = await kernelClient.patch(ENGAGEMENT_ENDPOINTS.MOOD_CHECKIN(checkinId), body);
  return data as MoodCheckin;
};

// ── E12d/R2 — sync: the call at sign-in and on app open ───────────────────────────────────

/**
 * What is waiting for me, in one call — and the write that makes it true.
 *
 * ⛔ **CALLING THIS IS WHAT ISSUES A SURVEY.** There is no scheduler on the kernel, by owner
 * ruling: the 30/60/90-day milestones are issued when the app talks to it. A milestone that
 * fell due while the app was closed appears on the first call after it, dated the day it fell
 * due. Skipping this call means the patient is never offered the survey at all.
 *
 * ⛔ **NOT A POLL.** R1 is *"in-app only for now, push later"* — the patient learns of a reply
 * when they open the app. Call it at sign-in and when the app comes to the foreground; calling
 * it on a timer would be a battery-powered poller pretending to be a notification system.
 *
 * ⚠️ It carries counts and a date. No subject, no body, no mood, no score.
 */
export interface EngagementSync {
  org_id: number | null;
  /** The PATIENT's local day, server-decided — the same one `listMyMoodCheckins` reports. */
  today: string;
  /** Issued BY THIS CALL. Normally 0; it is not a backlog count. */
  surveys_issued: number;
  surveys_outstanding: number;
  /** Staff replies not yet opened — the Messages badge. */
  unread_replies: number;
  checked_in_today: boolean;
}

/** Everything quiet — what the UI shows before the first answer and when the call fails. */
export const NO_ENGAGEMENT_SYNC: EngagementSync = {
  org_id: null,
  today: "",
  surveys_issued: 0,
  surveys_outstanding: 0,
  unread_replies: 0,
  checked_in_today: true,
};

export const syncEngagement = async (): Promise<EngagementSync> => {
  const { data } = await kernelClient.post(ENGAGEMENT_ENDPOINTS.SYNC);
  return data as EngagementSync;
};

// ── URS_12.3 — surveys ────────────────────────────────────────────────────────────────────

export interface SurveyResponse {
  id: number;
  submitted_at: string;
  score_pct: number | null;
  free_text_response: string | null;
  answers: Record<string, unknown>;
  source: EngagementRef;
  /** F-536 — the answers the kernel flagged for staff attention (ids and option codes). */
  flags?: Array<{ question_id: string; options: string[]; creates_task: boolean }>;
}

export interface Survey {
  id: number;
  profile_id: number;
  org_id: number;
  cadence: CadenceRef;
  scheduled_for: string;
  sent_at: string | null;
  completed_at: string | null;
  /** F-536 — the questionnaire document (`{title, questions: [...]}`); `{}` on a survey issued
   *  before the instruments existed. Rendered by `pages/engagement/SurveyQuestionnaire`. */
  questions: Record<string, unknown>;
  /** F-536 — the instrument version, `null` for a pre-instrument survey. */
  instrument_id?: number | null;
  /** `null` IS the outstanding state — AC 4. */
  response: SurveyResponse | null;
}

export interface MySurveys {
  items: Survey[];
  total: number;
  outstanding: number;
}

export const listMySurveys = async (): Promise<MySurveys> => {
  const { data } = await kernelClient.get(ENGAGEMENT_ENDPOINTS.SURVEYS);
  return data as MySurveys;
};

export const submitSurveyResponse = async (
  surveyId: number,
  answers: Record<string, unknown>,
  scorePct: number | null,
  freeText: string | null,
): Promise<SurveyResponse> => {
  const { data } = await kernelClient.post(
    ENGAGEMENT_ENDPOINTS.SURVEY_RESPONSE(surveyId),
    {
      answers,
      score_pct: scorePct,
      free_text_response: freeText && freeText.trim() ? freeText.trim() : null,
    },
  );
  return data as SurveyResponse;
};

// ── URS_12.7 / ACDPA-322 — goals ──────────────────────────────────────────────────────────

/**
 * One goal VERSION. ⛔ Goals are versioned and never edited in place, so a list contains the
 * history; `is_active` is the one in force.
 */
export interface PatientGoal {
  goal_id: number;
  goal_type: string;
  /** Rendered by the kernel — "BiWaze Cough", "Sleep". Do not map codes in this app. */
  label: string;
  measurement_unit: string;
  target_count: number | null;
  target_seconds: number | null;
  target_hours: number | null;
  days_of_week: number[];
  effective_from: string;
  effective_to: string | null;
  is_active: boolean;
  created_by_profile_id: number | null;
  created_at: string | null;
}

export const listMyGoals = async (activeOnly = false): Promise<PatientGoal[]> => {
  const { data } = await kernelClient.get(ENGAGEMENT_ENDPOINTS.GOALS, {
    params: activeOnly ? { active_only: true } : undefined,
  });
  return (data?.items ?? []) as PatientGoal[];
};

export const setMyGoal = async (goal: {
  goal_type: string;
  measurement_unit: string;
  target_count?: number | null;
  target_seconds?: number | null;
  days_of_week?: number[] | null;
  effective_from?: string | null;
}): Promise<PatientGoal> => {
  const { data } = await kernelClient.put(ENGAGEMENT_ENDPOINTS.GOALS, goal);
  return data as PatientGoal;
};

// ── error helper ──────────────────────────────────────────────────────────────────────────

/**
 * The kernel's `{error, code}` turned into something a patient can read.
 *
 * ⭐ THE SERVER'S SENTENCE WINS. Every code below already has a human message written next to
 * it in the kernel, phrased for this audience; re-writing them here would be two wordings to
 * keep in step. This function exists for the cases the server cannot phrase — no network, and
 * the catch-all — and to keep a raw axios string off the screen.
 */
export const engagementErrorMessage = (
  error: unknown,
  fallback = "Something went wrong. Please try again.",
): string => {
  const err = error as {
    response?: { status?: number; data?: { error?: string; code?: string } };
    message?: string;
  };
  if (!err?.response) {
    return err?.message === "No network connection"
      ? "You are offline. Your message has not been sent."
      : fallback;
  }
  return err.response.data?.error || fallback;
};

/** The kernel's machine-readable reason, for the few branches the UI treats differently. */
export const engagementErrorCode = (error: unknown): string | null => {
  const err = error as { response?: { data?: { code?: string } } };
  return err?.response?.data?.code ?? null;
};
